import { useCurrentPageAccess } from "@/app/router";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import { Input } from "@/shared/components/ui/input";
import { LoadingSpinner } from "@/shared/components/feedback/LoadingSpinner";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { ArrowLeft, Plus, RotateCw, Trash2, AlertCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useCreateErrorPatternMutation,
  useDeleteErrorPatternsMutation,
  useErrorPatternsQuery,
  useUpdateErrorPatternMutation,
} from "@/hooks/useErrorPatterns";
import type { ErrorPatternRow } from "@/types/collection-types";
import { CHANNELS } from "@/constants/channels";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/shared/components/table/DataTableColumnHeader";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";

export function CollectionsErrorPatternsPage() {
  const { data, isLoading, isError, refetch } = useErrorPatternsQuery();
  const { canWriteCurrentPage } = useCurrentPageAccess();

  const createMutation = useCreateErrorPatternMutation();
  const updateMutation = useUpdateErrorPatternMutation();
  const deleteMutation = useDeleteErrorPatternsMutation();

  // 페이지네이션 및 정렬
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);

  // 검색
  const [searchKeyword, setSearchKeyword] = useState("");

  // 모달 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "update">("create");
  const [editingPattern, setEditingPattern] = useState<ErrorPatternRow | null>(null);

  // 폼 상태
  const [formChannel, setFormChannel] = useState<string>("");
  const [formPattern, setFormPattern] = useState<string>("");
  const [formDescription, setFormDescription] = useState<string>("");
  const [formError, setFormError] = useState<string>("");

  // 선택된 행
  const [selectedRows, setSelectedRows] = useState<ErrorPatternRow[]>([]);

  // "all"을 포함한 채널 목록
  const channelsWithAll = useMemo(() => ["all", ...CHANNELS], []);

  // 검색 필터
  const filteredPatterns = useMemo(() => {
    if (!data?.patterns) return [];
    if (!searchKeyword) return data.patterns;

    const keyword = searchKeyword.toLowerCase();
    return data.patterns.filter(
      (p) =>
        p.channel.toLowerCase().includes(keyword) ||
        p.pattern.toLowerCase().includes(keyword) ||
        (p.description && p.description.toLowerCase().includes(keyword))
    );
  }, [data?.patterns, searchKeyword]);

  // 정렬
  const sortedPatterns = useMemo(() => {
    if (sorting.length === 0) return filteredPatterns;

    const sorted = [...filteredPatterns];
    const sort = sorting[0];

    sorted.sort((a, b) => {
      const aValue = a[sort.id as keyof typeof a];
      const bValue = b[sort.id as keyof typeof b];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) return sort.desc ? 1 : -1;
      if (aValue > bValue) return sort.desc ? -1 : 1;
      return 0;
    });

    return sorted;
  }, [filteredPatterns, sorting]);

  // 페이지네이션
  const paginatedPatterns = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return sortedPatterns.slice(start, end);
  }, [sortedPatterns, page, pageSize]);

  // 컬럼 정의
  const columns = useMemo<ColumnDef<ErrorPatternRow>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        size: 50,
        minSize: 50,
        maxSize: 50,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
      },
      {
        accessorKey: "id",
        header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
      },
      {
        accessorKey: "channel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="채널" />,
      },
      {
        accessorKey: "pattern",
        header: ({ column }) => <DataTableColumnHeader column={column} title="패턴" />,
        cell: ({ row }) => (
          <div className="max-w-[500px] truncate font-mono text-sm">{row.original.pattern}</div>
        ),
      },
      {
        accessorKey: "description",
        header: ({ column }) => <DataTableColumnHeader column={column} title="비고" />,
        cell: ({ row }) => (
          <div className="max-w-[200px] truncate text-sm text-muted-foreground">
            {row.original.description || "-"}
          </div>
        ),
      },
    ],
    []
  );

  // 모달 열기 (생성)
  const handleOpenCreateDialog = () => {
    setDialogMode("create");
    setEditingPattern(null);
    setFormChannel("");
    setFormPattern("");
    setFormDescription("");
    setFormError("");
    setDialogOpen(true);
  };

  // 모달 열기 (수정)
  const handleOpenUpdateDialog = (pattern: ErrorPatternRow) => {
    setDialogMode("update");
    setEditingPattern(pattern);
    setFormChannel(pattern.channel);
    setFormPattern(pattern.pattern);
    setFormDescription(pattern.description || "");
    setFormError("");
    setDialogOpen(true);
  };

  // 모달 닫기
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormError("");
  };

  // 폼 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!formChannel || !formPattern) {
      setFormError("모든 필드를 입력해주세요.");
      return;
    }

    try {
      if (dialogMode === "create") {
        await createMutation.mutateAsync({
          channel: formChannel,
          pattern: formPattern,
          description: formDescription || null,
        });
      } else if (editingPattern) {
        await updateMutation.mutateAsync({
          patternId: editingPattern.id,
          request: {
            channel: formChannel,
            pattern: formPattern,
            description: formDescription || null,
          },
        });
      }
      handleCloseDialog();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "작업에 실패했습니다.");
    }
  };

  // 삭제
  const handleDelete = async () => {
    if (selectedRows.length === 0) return;

    if (!confirm(`선택한 ${selectedRows.length}개의 패턴을 삭제하시겠습니까?`)) {
      return;
    }

    const validIds = selectedRows.filter((r) => r && r.id).map((r) => r.id);

    if (validIds.length === 0) {
      return;
    }

    await deleteMutation.mutateAsync({
      ids: validIds,
    });
    setSelectedRows([]);
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div>
        <Breadcrumbs />
        <PageHeader
          title="오류 패턴 관리"
          description="수집 오류를 자동으로 분류하기 위한 패턴을 관리합니다."
        />
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // 에러 상태
  if (isError) {
    return (
      <div>
        <Breadcrumbs />
        <PageHeader
          title="오류 패턴 관리"
          description="수집 오류를 자동으로 분류하기 위한 패턴을 관리합니다."
        />
        <EmptyState
          title="데이터를 불러올 수 없습니다"
          description="오류가 발생했습니다. 다시 시도해주세요."
          action={
            <Button variant="outline" onClick={() => refetch()}>
              다시 시도
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumbs />
      <PageHeader
        title="오류 패턴 관리"
        description="수집 오류를 자동으로 분류하기 위한 패턴을 관리합니다."
        actions={
          <>
            <Button variant="outline" size="icon" asChild>
              <Link to="/collections/errors">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" size="icon" onClick={() => refetch()}>
              <RotateCw className="h-4 w-4" />
            </Button>
            <Button onClick={handleOpenCreateDialog} disabled={!canWriteCurrentPage}>
              <Plus className="mr-2 h-4 w-4" />
              추가
            </Button>
            <Button
              variant="destructive"
              disabled={
                !canWriteCurrentPage || selectedRows.length === 0 || deleteMutation.isPending
              }
              onClick={handleDelete}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              삭제 ({selectedRows.length})
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        data={paginatedPatterns}
        totalCount={sortedPatterns.length}
        page={page}
        pageSize={pageSize}
        sorting={sorting}
        onSortingChange={setSorting}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={false}
        isError={false}
        enableRowSelection
        getRowId={(row) => row.id.toString()}
        onRowSelectionChange={setSelectedRows}
        onRowClick={canWriteCurrentPage ? handleOpenUpdateDialog : undefined}
        toolbar={
          <DataTableToolbar
            searchValue={searchKeyword}
            onSearchChange={(v) => {
              setSearchKeyword(v);
              setPage(1);
            }}
            showReset={!!searchKeyword}
            onReset={() => {
              setSearchKeyword("");
              setPage(1);
            }}
            searchPlaceholder="패턴 검색 (channel, pattern...)"
          />
        }
      />

      {/* 생성/수정 다이얼로그 */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{dialogMode === "create" ? "패턴 추가" : "패턴 수정"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">채널</label>
              <Select value={formChannel} onValueChange={setFormChannel} required>
                <SelectTrigger>
                  <SelectValue placeholder="채널 선택" />
                </SelectTrigger>
                <SelectContent>
                  {channelsWithAll.map((channel) => (
                    <SelectItem key={channel} value={channel}>
                      {channel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">패턴</label>
              <Textarea
                value={formPattern}
                onChange={(e) => setFormPattern(e.target.value)}
                placeholder="정규 표현식 입력..."
                rows={6}
                maxLength={1024}
                required
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                정규 표현식을 사용합니다. 오류 메시지에서 매칭할 패턴을 입력하세요.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">비고</label>
              <Input
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="비고 입력 (선택사항)"
                maxLength={512}
              />
            </div>

            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                취소
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {dialogMode === "create" ? "추가" : "수정"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
